import { Injectable } from "@angular/core";
import { AccountsService } from "@services/accounts.service";
import { ExportOrdersService } from "@services/dws/export-orders.service";
import { ExportPreordersService } from "@services/dws/export-preorders.service";
import { GiftsService } from "@services/dws/gifts.service";
import { ExportGift } from "@interfaces/dws/gift";
import { HostReservationFormOptionsService } from "@services/host-reservation-form-options/host-reservation-form-options.service";
import { AdditionalQuestionsService } from "@services/dws/additional-questions.service";
import { SchedulingService } from "@services/dws/scheduling/page-scheduling/page-schedulings.service";
import { ReservationExporting } from "@interfaces/dws/scheduling/reservation-exporting";
import { locale } from "@util/locale";
import { saveXLSX } from "@util/save-xlsx";
import { toast } from "@web/util/toast";
import { observe } from "@web/util/loading/loading";
import { tr } from "@util/tr";
import { activeWineryChannels, isWineryPriceListChannel } from "@services/dws/winery-channel.util";
import { WineryChannelsService } from "@services/dws/winery-channels.service";
import { PaymentsListExportService } from "@web/app/pages/payments/payments-list-export.service";
import { PayoutsListExportService } from "@web/app/pages/payments/payouts-list-export.service";
import { ExportReceiptsService } from "@services/dws/export-receipts.service";
import { ReceiptsService, ReceiptFilter } from "@services/dws/receipts.service";
import * as moment from "moment";
import { switchMap } from "rxjs/operators";
import {
  AiAgentUiCommand,
  AiAgentUiCommandContext,
  AiAgentUiCommandHandler,
} from "../ai-agent-ui-command.types";

/**
 * Runs the same FE export path as CRM list pages (filters from the agent tool).
 * No dependency on list page components being open.
 */
@Injectable({ providedIn: "root" })
export class ExportListExcelHandler implements AiAgentUiCommandHandler {
  constructor(
    private accounts: AccountsService,
    private exportOrders: ExportOrdersService,
    private exportPreorders: ExportPreordersService,
    private giftsService: GiftsService,
    private wineryChannels: WineryChannelsService,
    private scheduling: SchedulingService,
    private formOptions: HostReservationFormOptionsService,
    private additionalQuestions: AdditionalQuestionsService,
    private paymentsListExport: PaymentsListExportService,
    private payoutsListExport: PayoutsListExportService,
    private receiptsService: ReceiptsService,
    private exportReceipts: ExportReceiptsService
  ) {}

  supports(type: string): boolean {
    return type === "exportListExcel";
  }

  async handle(cmd: AiAgentUiCommand, _ctx?: AiAgentUiCommandContext): Promise<void> {
    const list = cmd.list != null ? String(cmd.list).trim() : "";
    if (!list) {
      void toast(tr("Error"));
      return;
    }
    // Immediate feedback (panel z-index used to hide snackbars behind the shell).
    void toast(tr("We are preparing your export..."));
    const login = await this.accounts.getLogin();
    const company = login?.company;
    if (!company?.id) {
      void toast(tr("Error"));
      return;
    }
    const filters = this.normalizeFilters(
      (cmd.filters && typeof cmd.filters === "object"
        ? (cmd.filters as Record<string, unknown>)
        : {}) as Record<string, any>,
      list
    );

    switch (list) {
      case "orders":
      case "shipping":
        this.exportOrders.export("xlsx", company, filters as any);
        return;
      case "preorders":
        this.exportPreordersWithChannels(company, filters);
        return;
      case "gifts":
        this.exportGifts(company.id, filters);
        return;
      case "reservations":
      case "calendar":
        this.exportReservations(company.id, filters);
        return;
      case "payments":
        this.paymentsListExport.startExport({
          wineryId: company.id,
          query: { ...filters },
          format: "xlsx",
        });
        return;
      case "payouts":
        this.payoutsListExport.startExport({
          wineryId: company.id,
          query: { ...filters },
          format: "xlsx",
        });
        return;
      case "receipts":
        this.exportReceiptsList(company, filters);
        return;
      default:
        toast(tr("Error"));
    }
  }

  /**
   * Map common agent date keys onto the CRM list export filter names.
   * Reservations/calendar use startTime/endTime (YYYY-MM-DD).
   */
  private normalizeFilters(filters: Record<string, any>, list: string): Record<string, any> {
    const out = { ...filters };
    const start =
      out.startTime || out.startDate || out.dateFrom || out.from || out.start;
    const end = out.endTime || out.endDate || out.dateTo || out.to || out.end;
    if (list === "reservations" || list === "calendar") {
      if (start) {
        out.startTime = String(start).slice(0, 10);
      }
      if (end) {
        out.endTime = String(end).slice(0, 10);
      }
      delete out.startDate;
      delete out.endDate;
      delete out.dateFrom;
      delete out.dateTo;
      delete out.from;
      delete out.to;
      delete out.start;
      delete out.end;
    } else if (list === "receipts") {
      if (start && !out.startDate) {
        out.startDate = String(start).slice(0, 10);
      }
      if (end && !out.endDate) {
        out.endDate = String(end).slice(0, 10);
      }
    }
    return out;
  }

  private exportPreordersWithChannels(company: { id: string }, filters: Record<string, any>): void {
    const preorderFilters = {
      field: "createdAt",
      direction: "DESC" as const,
      ...filters,
      wineryId: company.id,
    };
    observe(this.wineryChannels.list({ wineryId: company.id }, 0, 999)).subscribe({
      next: (page) => {
        const channels = activeWineryChannels(page?.content || []).filter(isWineryPriceListChannel);
        this.exportPreorders.export("xlsx", company as any, preorderFilters as any, channels);
      },
      error: () => {
        this.exportPreorders.export("xlsx", company as any, preorderFilters as any, []);
      },
    });
  }

  private exportGifts(wineryId: string, filters: Record<string, any>): void {
    observe(this.giftsService.giftsToExport(wineryId, filters)).subscribe({
      next: (gifts: ExportGift[]) => {
        if (!gifts?.length) {
          toast(tr("No reservations found in this period"));
          return;
        }
        const rows = gifts.map((g) => ({ ...g }));
        saveXLSX(rows as any, "gifts.xlsx");
      },
      error: () => toast(tr("Error")),
    });
  }

  private exportReservations(wineryId: string, filters: Record<string, any>): void {
    const finalFilters = { ...filters };
    const exportObservable = this.scheduling
      .list(wineryId, finalFilters, 0, 999999, "createdAt,DESC")
      .pipe(
        switchMap((list) =>
          ReservationExporting.loadAdditionalQuestionsForExport(
            list.content,
            (reservationIds) =>
              this.additionalQuestions.exportForReservations(
                wineryId,
                reservationIds,
                (locale().locale || "it").toLowerCase()
              ),
            (id) => this.additionalQuestions.listReservationAnswers(id)
          )
        )
      );

    observe(exportObservable).subscribe({
      next: ({ rows: allData, aqHeaders }) => {
        if (!allData?.length) {
          toast(tr("No reservations found to export"));
          return;
        }
        observe(this.formOptions.get(wineryId, "")).subscribe({
          next: (formOptions) => {
            try {
              ReservationExporting.saveAsXLSX(allData, formOptions, aqHeaders);
            } catch {
              toast(tr("Errore durante l'export"));
            }
          },
          error: () => toast(tr("Error loading form options for export")),
        });
      },
      error: () => toast(tr("Error")),
    });
  }

  private exportReceiptsList(company: { id: string; name?: string }, filters: Record<string, any>): void {
    const start = filters.startDate || filters.dateFrom;
    const end = filters.endDate || filters.dateTo;
    if (!start || !end) {
      toast(tr("Select period for SDI Log Export"));
      return;
    }
    const startDate = moment(start).startOf("day").toDate();
    const endDate = moment(end).endOf("day").toDate();
    const receiptFilters: ReceiptFilter = {
      wineryId: company.id,
      startDate: moment(startDate).toISOString(),
      endDate: moment(endDate).toISOString(),
      ...filters,
      count: 999999,
      page: 0,
    };
    observe(this.receiptsService.listv2(receiptFilters)).subscribe({
      next: (page) => {
        if (!page?.content?.length) {
          toast(tr("No documents found in this period"));
          return;
        }
        this.exportReceipts.exportToXLSX(page.content, {
          wineryChannels: [],
          pointOfSales: [],
          startDate,
          endDate,
          appliedFilters: filters,
        });
      },
      error: () => toast(tr("Error")),
    });
  }
}
